import { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORT_SENDER } from "../../utils/constants/support";
import { formatDateTime } from "../../utils/helpers/dateDisplay";
import AttachmentList from "./AttachmentList";
import MessageEditor from "./MessageEditor";

const URL_PATTERN = /(https?:\/\/[^\s<]+[^\s<.,;:!?)\]'"])/g;

// Message text with bare http(s) links made clickable. Everything else stays
// text — React escapes it, nothing is ever rendered as HTML.
function Linkified({ text, onOpenLink }) {
  const parts = text.split(URL_PATTERN);
  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <a
        key={index}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          if (!onOpenLink) return;
          e.preventDefault();
          onOpenLink(part);
        }}
        className="underline underline-offset-2 break-all"
      >
        {part}
      </a>
    ) : (
      <Fragment key={index}>{part}</Fragment>
    ),
  );
}

// A status change in the thread. Its wording is the client's, keyed by the
// event code and by who is reading — "you closed it" on the site is
// "the user closed it" in the Mini App.
function SystemLine({ message, viewer }) {
  const { t } = useTranslation();
  return (
    <li className="flex justify-center">
      <span className="text-[11px] font-semibold text-[color:var(--support-muted)] bg-[color:var(--support-chip)] px-3 py-1 rounded-full text-center">
        {t(`support.events.${viewer}.${message.system_event}`, {
          name: message.agent_name || "",
        })}
        <span className="opacity-70"> · {formatDateTime(message.created_at)}</span>
      </span>
    </li>
  );
}

/**
 * A ticket's thread, from one side of the conversation.
 *
 * `viewer` is "user" (the site) or "agent" (the Mini App); it decides which
 * bubbles are "mine" (right, accent) and how authors are named. `authorName`
 * names the ticket author in the agent view. `onOpenLink` overrides opening
 * links and attachments (the Mini App routes them through Telegram).
 *
 * `onEdit(message, body)`, when given, offers "Edit" on each message the
 * server marks `can_edit` (the reader's own) and must return a promise that
 * rejects when the edit failed, so the editor keeps the draft. An edited
 * message says so, on both sides. `compact`: the Mini App's narrow layout.
 */
export default function MessageThread({ messages, viewer, authorName, onOpenLink, onEdit, compact = false }) {
  const { t } = useTranslation();
  // The uuid of the message being edited: one at a time. The thread re-reads
  // itself on a timer; the open editor and its draft outlive that.
  const [editing, setEditing] = useState(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const save = async (message, body) => {
    await onEdit(message, body);
    if (alive.current) setEditing((uuid) => (uuid === message.uuid ? null : uuid));
  };

  const nameOf = (message) => {
    if (message.sender === SUPPORT_SENDER.AGENT) {
      return viewer === "agent"
        ? message.agent_name
        : t("support.thread.agentName", { name: message.agent_name });
    }
    return viewer === "user" ? t("support.thread.you") : authorName;
  };

  return (
    <ol className="space-y-4">
      {messages.map((message) => {
        if (message.sender === SUPPORT_SENDER.SYSTEM) {
          return <SystemLine key={message.uuid} message={message} viewer={viewer} />;
        }
        const mine =
          viewer === "user"
            ? message.sender === SUPPORT_SENDER.USER
            : message.sender === SUPPORT_SENDER.AGENT;
        const canEdit = !!onEdit && !!message.can_edit;
        const isEditing = canEdit && editing === message.uuid;
        return (
          <li key={message.uuid} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
            <div
              className={`flex flex-col min-w-0 ${
                isEditing ? "w-full sm:w-[75%]" : "max-w-[88%] sm:max-w-[75%]"
              } ${mine ? "items-end" : "items-start"}`}
            >
              <span className="text-[11px] font-semibold text-[color:var(--support-muted)] mb-1 px-1">
                {nameOf(message)} · {formatDateTime(message.created_at)}
                {message.edited_at && (
                  <span title={t("support.thread.editedAt", { date: formatDateTime(message.edited_at) })}>
                    {" "}
                    · {t("support.thread.edited")}
                  </span>
                )}
                {canEdit && !isEditing && (
                  <>
                    {" "}
                    ·{" "}
                    <button
                      type="button"
                      onClick={() => setEditing(message.uuid)}
                      aria-label={t("support.thread.editLabel")}
                      className="inline-flex items-center gap-0.5 font-bold text-[color:var(--support-mine-bg)] hover:underline focus-visible:outline-none focus-visible:underline"
                    >
                      <span aria-hidden="true">✎</span>
                      {t("support.thread.edit")}
                    </button>
                  </>
                )}
              </span>
              {isEditing ? (
                <>
                  <MessageEditor
                    initialBody={message.body}
                    hasFiles={!!message.attachments?.length}
                    onSave={(body) => save(message, body)}
                    onCancel={() => setEditing(null)}
                    compact={compact}
                  />
                  {/* They stay as they are: only the text is edited. */}
                  <AttachmentList attachments={message.attachments} onOpen={onOpenLink} className="mt-2" />
                </>
              ) : (
                <div
                  className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed min-w-0 max-w-full ${
                    mine
                      ? "rounded-br-md bg-[color:var(--support-mine-bg)] text-[color:var(--support-mine-text)]"
                      : "rounded-bl-md border border-[color:var(--support-border)] bg-[color:var(--support-surface)] text-[color:var(--support-surface-text)]"
                  }`}
                >
                  {message.body && (
                    <p className="whitespace-pre-wrap break-words">
                      <Linkified text={message.body} onOpenLink={onOpenLink} />
                    </p>
                  )}
                  <AttachmentList
                    attachments={message.attachments}
                    onOpen={onOpenLink}
                    inverted={mine}
                    className={message.body ? "mt-2" : ""}
                  />
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
